// @ts-ignore - axios types may not be recognized
import axios, { AxiosInstance } from 'axios';
import { MetricsLiteVisitor } from '../types';
import { getMetricsLiteApiKey } from './appSettings';

class MetricsLiteClient {
  private apiUrl: string;
  private apiKey: string;
  private client: AxiosInstance;
  private instanceId: string;

  constructor() {
    this.instanceId = Math.random().toString(36).substr(2, 9);

    // Hardcode the API URL - it's always the same for all users
    this.apiUrl = 'https://app.metricslite.com/api/v1';

    // Only configure API key from localStorage/environment
    const localStorageKey = localStorage.getItem('metrics_lite_api_key');
    const envKey = process.env.REACT_APP_METRICS_LITE_API_KEY;
    this.apiKey = (localStorageKey && localStorageKey.trim()) || (envKey && envKey.trim()) || '';

    // Debug logging disabled in production
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      // Only log in local development
    }

    this.client = axios.create({
      baseURL: this.apiUrl,
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
  }

  /**
   * Update API credentials (for dynamic configuration)
   */
  updateCredentials(apiUrl: string, apiKey: string) {
    this.apiUrl = apiUrl;
    this.apiKey = apiKey;
    this.client = axios.create({
      baseURL: this.apiUrl,
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
  }

  /**
   * Fetch comprehensive visitor data from Metrics Lite
   */
  async getVisitorData(visitorId: number): Promise<MetricsLiteVisitor | null> {
    this.refreshClient();
    const apiKey = this.getApiKey();

    if (!apiKey) {
      console.error('Metrics Lite API key is required');
      return null;
    }

    try {
      // Metrics Lite uses /leads/{id} endpoint with X-API-Key header
      const response = await this.client.get(`/leads/${visitorId}`, {
        headers: {
          'X-API-Key': apiKey,
        },
      });

      if (response.data.success && response.data.data) {
        return response.data.data as MetricsLiteVisitor;
      }

      // If response has error, log it
      if (response.data.error) {
        console.error('Metrics Lite API error:', response.data.error);
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching visitor data from Metrics Lite:', error);
      if (error.response?.data?.error) {
        console.error('API Error:', error.response.data.error);
      }
      return null;
    }
  }

  /**
   * Fetch comprehensive lead data from Metrics Lite with date range
   */
  async getComprehensiveLeadData(
    leadId: number,
    startDate?: string,
    endDate?: string
  ): Promise<MetricsLiteVisitor | null> {
    // #region agent log - hypothesis: client refresh and config check
    // Debug logging removed for production
    // #endregion

    this.refreshClient();
    const apiKey = this.getApiKey();

    if (!apiKey) {
      // #region agent log - hypothesis: API key missing
      // Debug logging removed for production
      // #endregion
      console.error('Metrics Lite API key is required');
      return null;
    }

    try {
      console.log('Metrics Lite: Starting getComprehensiveLeadData for leadId:', leadId);

      // Create full URL with date range for past two weeks
      const fullUrl = this.createApiUrlWithDateRange(leadId);
      console.log('Metrics Lite: Created full URL:', fullUrl);

      if (!fullUrl) {
        console.error('Metrics Lite: URL creation failed - no base URL configured');
        // #region agent log - hypothesis: URL creation failed
        // Debug logging removed for production
        // #endregion
        return null;
      }

      console.log('Metrics Lite: Making direct axios.get request to:', fullUrl);

      // #region agent log - hypothesis: request preparation
      // Debug logging removed for production
      // #endregion
      console.log('Fetching Metrics Lite data:', {
        baseUrl: this.getApiUrl(),
        fullUrl: fullUrl,
        leadId,
        hasApiKey: !!apiKey,
      });
      console.log('Metrics Lite Full API URL (for curl testing):', fullUrl);
      console.log('Curl command:', `curl -X 'GET' '${fullUrl}' -H 'accept: application/json' -H 'X-API-Key: YOUR_API_KEY'`);

      // Make direct request to full URL (bypassing base URL)
      console.log('Metrics Lite: About to call axios.get with URL:', fullUrl);
      const response = await axios.get(fullUrl, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        timeout: 10000,
      });
      console.log('Metrics Lite: axios.get completed, response status:', response.status);

      // #region agent log - hypothesis: response received
      // Debug logging removed for production
      // #endregion

      console.log('Metrics Lite API Response:', {
        status: response.status,
        success: response.data.success,
        hasData: !!response.data.data,
        dataKeys: response.data.data ? Object.keys(response.data.data) : [],
        error: response.data.error,
        fullResponse: response.data,
      });

      // #region agent log - hypothesis: response structure check
      // Debug logging removed for production
      // #endregion

      if (response.data.success && response.data.data) {
        // #region agent log - hypothesis: successful response with data
        // Debug logging removed for production
        // #endregion
        return response.data.data as MetricsLiteVisitor;
      }

      // If response has error, log it
      if (response.data.error) {
        // #region agent log - hypothesis: API returned error
        // Debug logging removed for production
        // #endregion
        console.error('Metrics Lite API error:', response.data.error);
      }

      // If no data but success is true, log the response structure
      if (response.data.success && !response.data.data) {
        // #region agent log - hypothesis: success but no data
        // Debug logging removed for production
        // #endregion
        console.warn('Metrics Lite returned success but no data. Response structure:', response.data);
      }

      // #region agent log - hypothesis: returning null
      // Debug logging removed for production
      // #endregion
      return null;
    } catch (error: any) {
      // #region agent log - hypothesis: exception occurred
      // Debug logging removed for production
      // #endregion
      console.error('Error fetching comprehensive lead data from Metrics Lite:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        headers: error.config?.headers,
      });

      if (error.response?.data?.error) {
        // #region agent log - hypothesis: API error in response
        // Debug logging removed for production
        // #endregion
        console.error('API Error:', error.response.data.error);
      }

      if (error.response?.status === 404) {
        // #region agent log - hypothesis: 404 - visitor not found
        // Debug logging removed for production
        // #endregion
        console.warn(`Visitor ID ${leadId} not found (404). The ID might be incorrect or the visitor might not exist.`);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        // #region agent log - hypothesis: auth failed
        // Debug logging removed for production
        // #endregion
        console.error('Authentication failed. Check your API key.');
      }

      return null;
    }
  }

  /**
   * Search for visitors by email
   */
  async searchVisitorsByEmail(email: string): Promise<MetricsLiteVisitor[]> {
    try {
      const response = await this.client.get('/visitors/search', {
        params: { email },
      });

      if (response.data.success && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      }

      return [];
    } catch (error) {
      console.error('Error searching visitors by email:', error);
      return [];
    }
  }

  /**
   * Search for visitors by name
   */
  async searchVisitorsByName(name: string): Promise<MetricsLiteVisitor[]> {
    this.refreshClient();
    const apiKey = this.getApiKey();

    if (!apiKey) {
      console.error('Metrics Lite API key is required');
      return [];
    }

    try {
      const response = await this.client.get('/leads/search', {
        headers: {
          'X-API-Key': apiKey,
        },
        params: { name },
      });

      if (response.data.success && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      }

      return [];
    } catch (error: any) {
      console.error('Error searching visitors by name:', error);
      if (error.response?.data?.error) {
        console.error('API Error:', error.response.data.error);
      }
      return [];
    }
  }

  /**
   * Search for visitors by IP address
   */
  async searchVisitorsByIP(ipAddress: string, hoursWindow: number = 24): Promise<MetricsLiteVisitor[]> {
    this.refreshClient();
    const apiKey = this.getApiKey();

    if (!apiKey) {
      console.error('Metrics Lite API key is required');
      return [];
    }

    try {
      const response = await this.client.get('/leads/search', {
        headers: {
          'X-API-Key': apiKey,
        },
        params: {
          ip_address: ipAddress,
          hours_window: hoursWindow,
        },
      });

      if (response.data.success && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      }

      return [];
    } catch (error: any) {
      console.error('Error searching visitors by IP:', error);
      if (error.response?.data?.error) {
        console.error('API Error:', error.response.data.error);
      }
      return [];
    }
  }

  /**
   * Check if API is configured (only checks for API key since URL is hardcoded)
   * Checks Firestore, localStorage, and environment variables
   */
  isConfigured(): boolean {
    // API URL is always available (hardcoded), only need to check API key
    const apiKey = this.getApiKey();
    return !!apiKey;
  }

  /**
   * Check if API is configured (async - checks Firestore)
   */
  async isConfiguredAsync(): Promise<boolean> {
    const apiKey = await this.getApiKeyAsync();
    return !!apiKey;
  }

  /**
   * Get current API URL (hardcoded - always the same)
   */
  getApiUrl(): string {
    // Return the hardcoded API URL
    const result = this.apiUrl;

    // Debug logging
    console.log('Metrics Lite getApiUrl debug:', {
      result,
      resultType: typeof result,
      resultLength: result ? result.length : 0
    });

    // #region agent log - hypothesis: getApiUrl debugging
    // Debug logging removed for production
    // #endregion

    return result;
  }

  /**
   * Get current API Key (checks Firestore first, then localStorage, then env)
   */
  getApiKey(): string {
    // For synchronous access, check localStorage first (will be migrated to Firestore)
    const localStorageKey = localStorage.getItem('metrics_lite_api_key');
    const envKey = process.env.REACT_APP_METRICS_LITE_API_KEY;
    // Treat empty strings as not set
    const effectiveLocalStorageKey = localStorageKey && localStorageKey.trim() ? localStorageKey : null;
    const effectiveEnvKey = envKey && envKey.trim() ? envKey : null;
    return effectiveLocalStorageKey || this.apiKey || effectiveEnvKey || '';
  }

  /**
   * Get API Key asynchronously from Firestore (preferred method)
   */
  async getApiKeyAsync(): Promise<string> {
    try {
      const dbKey = await getMetricsLiteApiKey();
      if (dbKey) {
        this.apiKey = dbKey;
        return dbKey;
      }
    } catch (error) {
      console.error('Error loading API key from Firestore:', error);
    }

    // Fallback to synchronous method
    return this.getApiKey();
  }

  /**
   * Create full API URL with date range for past two weeks
   */
  createApiUrlWithDateRange(leadId: number): string {
    console.log('Metrics Lite: createApiUrlWithDateRange called with leadId:', leadId);

    const baseUrl = this.getApiUrl();
    console.log('Metrics Lite: baseUrl from getApiUrl():', baseUrl);

    if (!baseUrl) {
      console.error('Metrics Lite: Base URL is empty/falsy');
      // #region agent log - hypothesis: base URL missing
      // Debug logging removed for production
      // #endregion
      return '';
    }

    // Calculate date range for past two weeks
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 14); // 14 days ago

    const startDateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
    const endDateStr = endDate.toISOString().split('T')[0]; // YYYY-MM-DD

    console.log('Metrics Lite: Date range calculated:', { startDateStr, endDateStr });

    const fullUrl = `${baseUrl}/leads/${leadId}/comprehensive?start_date=${startDateStr}&end_date=${endDateStr}`;

    console.log('Metrics Lite: Final full URL constructed:', fullUrl);

    // #region agent log - hypothesis: URL created successfully
    // Debug logging removed for production
    // #endregion

    return fullUrl;
  }

  /**
   * Refresh client with latest credentials (synchronous - uses localStorage/env)
   */
  refreshClient(): void {
    const apiUrl = this.getApiUrl();
    const apiKey = this.getApiKey();

    // Always update instance variables, even if empty
    this.apiUrl = apiUrl;
    this.apiKey = apiKey;

    // Only create client if we have both URL and key
    if (apiUrl && apiKey) {
      this.client = axios.create({
        baseURL: this.apiUrl,
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
    }
  }

  /**
   * Refresh client with latest credentials from Firestore (async - preferred)
   */
  async refreshClientAsync(): Promise<void> {
    const apiUrl = this.getApiUrl();
    // Try to load from Firestore first
    const apiKey = await this.getApiKeyAsync();

    // Always update instance variables, even if empty
    this.apiUrl = apiUrl;
    this.apiKey = apiKey;

    // Only create client if we have both URL and key
    if (apiUrl && apiKey) {
      this.client = axios.create({
        baseURL: this.apiUrl,
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
    }
  }

  /**
   * Get lead IDs by date range (for website activity counts)
   * Returns { lead_ids: number[], count: number }
   */
  async getLeadsByDateRange(startDate: string, endDate: string): Promise<{ lead_ids: number[]; count: number } | null> {
    this.refreshClient();
    const apiKey = this.getApiKey();

    if (!apiKey) {
      return null;
    }

    try {
      const response = await this.client.get('/leads', {
        headers: { 'X-API-Key': apiKey },
        params: { start_date: startDate, end_date: endDate },
      });

      if (response.data.success && response.data.data) {
        return {
          lead_ids: response.data.data.lead_ids || [],
          count: response.data.data.count || 0,
        };
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching leads by date range:', error.message);
      return null;
    }
  }
}

// Export singleton instance
export const metricsLiteClient = new MetricsLiteClient();

/**
 * Parse UTM parameters from a URL string
 */
function parseUtmFromUrl(url: string): { utmSource?: string; utmMedium?: string; utmCampaign?: string; utmTerm?: string; utmContent?: string } | null {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    const utmSource = params.get('utm_source') || undefined;
    const utmMedium = params.get('utm_medium') || undefined;
    const utmCampaign = params.get('utm_campaign') || undefined;
    const utmTerm = params.get('utm_term') || undefined;
    const utmContent = params.get('utm_content') || undefined;

    // Return null if no UTM parameters found
    if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent) {
      return null;
    }

    return { utmSource, utmMedium, utmCampaign, utmTerm, utmContent };
  } catch {
    // URL parsing failed - might be relative URL or malformed
    // Try to parse query string manually
    const queryMatch = url.match(/\?(.+)$/);
    if (!queryMatch) return null;

    const queryString = queryMatch[1];
    const params = new URLSearchParams(queryString);

    const utmSource = params.get('utm_source') || undefined;
    const utmMedium = params.get('utm_medium') || undefined;
    const utmCampaign = params.get('utm_campaign') || undefined;
    const utmTerm = params.get('utm_term') || undefined;
    const utmContent = params.get('utm_content') || undefined;

    if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent) {
      return null;
    }

    return { utmSource, utmMedium, utmCampaign, utmTerm, utmContent };
  }
}

/**
 * Extract all UTM touches from visitor events
 */
function extractUtmTouchesFromEvents(visitor: MetricsLiteVisitor): Array<{
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  timestamp: Date;
  url: string;
}> {
  const touches: Array<{
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    timestamp: Date;
    url: string;
  }> = [];

  if (!visitor.recent_events) return touches;

  // Handle case where recent_events might not be an array
  let eventsArray = visitor.recent_events;
  if (!Array.isArray(visitor.recent_events)) {
    if (typeof visitor.recent_events === 'object' && visitor.recent_events !== null) {
      eventsArray = Object.values(visitor.recent_events);
    } else {
      return touches;
    }
  }

  for (const event of eventsArray) {
    if (!event || !event.url) continue;

    const utmParams = parseUtmFromUrl(event.url);
    if (utmParams) {
      // Parse timestamp
      let timestamp: Date;
      if (event.created_at) {
        if (typeof event.created_at === 'number') {
          timestamp = new Date(event.created_at * (event.created_at < 1e10 ? 1000 : 1));
        } else {
          timestamp = new Date(event.created_at);
        }
      } else {
        timestamp = new Date();
      }

      touches.push({
        ...utmParams,
        timestamp,
        url: event.url,
      });
    }
  }

  // Sort by timestamp (earliest first)
  touches.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return touches;
}

/**
 * Transform Metrics Lite visitor data to CRM tracking data format
 * Now includes multi-touch UTM attribution (first, mid, last touch)
 */
export function transformVisitorToTrackingData(visitor: MetricsLiteVisitor) {
  const leadInfo = visitor.lead_info;

  // Helper function to safely parse dates
  const parseDate = (dateString: string | undefined): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return undefined;
    }
    return date;
  };

  // Extract UTM touches from event URLs
  const eventUtmTouches = extractUtmTouchesFromEvents(visitor);
  console.log(`Found ${eventUtmTouches.length} UTM touches in events`);

  // Build first touch UTM (from lead_info or first event with UTM)
  const leadInfoHasUtm = leadInfo.utm_source || leadInfo.utm_medium || leadInfo.utm_campaign;
  const firstTouchUtm: import('../types').UtmTouch | undefined = leadInfoHasUtm
    ? {
      utmSource: leadInfo.utm_source,
      utmMedium: leadInfo.utm_medium,
      utmCampaign: leadInfo.utm_campaign,
      timestamp: parseDate(leadInfo.first_seen),
      touchType: 'first' as const,
    }
    : eventUtmTouches.length > 0
      ? {
        utmSource: eventUtmTouches[0].utmSource,
        utmMedium: eventUtmTouches[0].utmMedium,
        utmCampaign: eventUtmTouches[0].utmCampaign,
        utmTerm: eventUtmTouches[0].utmTerm,
        utmContent: eventUtmTouches[0].utmContent,
        timestamp: eventUtmTouches[0].timestamp,
        url: eventUtmTouches[0].url,
        touchType: 'first' as const,
      }
      : undefined;

  // Build last touch UTM (most recent event with UTM)
  const lastEventTouch = eventUtmTouches.length > 0 ? eventUtmTouches[eventUtmTouches.length - 1] : null;
  const lastTouchUtm: import('../types').UtmTouch | undefined = lastEventTouch
    ? {
      utmSource: lastEventTouch.utmSource,
      utmMedium: lastEventTouch.utmMedium,
      utmCampaign: lastEventTouch.utmCampaign,
      utmTerm: lastEventTouch.utmTerm,
      utmContent: lastEventTouch.utmContent,
      timestamp: lastEventTouch.timestamp,
      url: lastEventTouch.url,
      touchType: 'last' as const,
    }
    : leadInfoHasUtm
      ? {
        utmSource: leadInfo.utm_source,
        utmMedium: leadInfo.utm_medium,
        utmCampaign: leadInfo.utm_campaign,
        timestamp: parseDate(leadInfo.last_seen),
        touchType: 'last' as const,
      }
      : undefined;

  // Build all UTM touches array with proper touch types
  const allUtmTouches: import('../types').UtmTouch[] = [];

  // Add first touch from lead_info if it has UTM
  if (leadInfoHasUtm) {
    allUtmTouches.push({
      utmSource: leadInfo.utm_source,
      utmMedium: leadInfo.utm_medium,
      utmCampaign: leadInfo.utm_campaign,
      timestamp: parseDate(leadInfo.first_seen),
      touchType: 'first',
    });
  }

  // Add event-based touches
  eventUtmTouches.forEach((touch, index) => {
    const isFirst = index === 0 && !leadInfoHasUtm;
    const isLast = index === eventUtmTouches.length - 1;

    allUtmTouches.push({
      utmSource: touch.utmSource,
      utmMedium: touch.utmMedium,
      utmCampaign: touch.utmCampaign,
      utmTerm: touch.utmTerm,
      utmContent: touch.utmContent,
      timestamp: touch.timestamp,
      url: touch.url,
      touchType: isFirst ? 'first' : isLast ? 'last' : 'mid',
    });
  });

  // Sort all touches by timestamp
  allUtmTouches.sort((a, b) => {
    const aTime = a.timestamp?.getTime() || 0;
    const bTime = b.timestamp?.getTime() || 0;
    return aTime - bTime;
  });

  // Update touch types after sorting
  if (allUtmTouches.length > 0) {
    allUtmTouches[0].touchType = 'first';
    if (allUtmTouches.length > 1) {
      allUtmTouches[allUtmTouches.length - 1].touchType = 'last';
      for (let i = 1; i < allUtmTouches.length - 1; i++) {
        allUtmTouches[i].touchType = 'mid';
      }
    }
  }

  // Use first touch for legacy fields (backward compatibility)
  const legacyUtmSource = firstTouchUtm?.utmSource || leadInfo.utm_source;
  const legacyUtmMedium = firstTouchUtm?.utmMedium || leadInfo.utm_medium;
  const legacyUtmCampaign = firstTouchUtm?.utmCampaign || leadInfo.utm_campaign;

  console.log('UTM Attribution Summary:', {
    firstTouch: firstTouchUtm ? `${firstTouchUtm.utmSource}/${firstTouchUtm.utmMedium}/${firstTouchUtm.utmCampaign}` : 'none',
    lastTouch: lastTouchUtm ? `${lastTouchUtm.utmSource}/${lastTouchUtm.utmMedium}/${lastTouchUtm.utmCampaign}` : 'none',
    totalTouches: allUtmTouches.length,
  });

  return {
    ipAddress: leadInfo.ip_address,
    userAgent: leadInfo.user_agent,
    screenWidth: leadInfo.screen_width,
    screenHeight: leadInfo.screen_height,
    language: leadInfo.language,
    timezone: leadInfo.timezone,
    // Legacy fields (first touch for backward compatibility)
    utmSource: legacyUtmSource,
    utmMedium: legacyUtmMedium,
    utmCampaign: legacyUtmCampaign,
    referrer: leadInfo.referrer,
    firstSeen: parseDate(leadInfo.first_seen),
    lastSeen: parseDate(leadInfo.last_seen),
    // Multi-touch attribution
    firstTouchUtm,
    lastTouchUtm,
    allUtmTouches: allUtmTouches.length > 0 ? allUtmTouches : undefined,
  };
}

/**
 * Transform Metrics Lite statistics to CRM format
 */
export function transformVisitorStatistics(visitor: MetricsLiteVisitor) {
  return {
    totalEvents: visitor.statistics.total_events,
    pageviews: visitor.statistics.pageviews,
    goalCompletions: visitor.statistics.goal_completions,
    totalRevenue: visitor.statistics.total_revenue,
  };
}

/**
 * Transform Metrics Lite events to CRM format
 */
export function transformVisitorEvents(visitor: MetricsLiteVisitor) {
  // Ensure recent_events exists and is an array
  if (!visitor.recent_events) {
    return [];
  }

  // Handle case where recent_events might not be an array
  let eventsArray = visitor.recent_events;
  if (!Array.isArray(visitor.recent_events)) {
    console.warn('Metrics Lite recent_events is not an array:', typeof visitor.recent_events, visitor.recent_events);
    // If it's an object with numeric keys, convert to array
    if (typeof visitor.recent_events === 'object' && visitor.recent_events !== null) {
      eventsArray = Object.values(visitor.recent_events);
      console.log('Converted recent_events object to array with', eventsArray.length, 'events');
    } else {
      return [];
    }
  }

  return eventsArray
    .filter(event => event != null) // Filter out null/undefined events
    .map(event => {
      // Handle MetricsLite timestamps - they may be in GMT or Unix timestamp format
      let createdAt: Date;

      if (event.created_at) {
        // Try different parsing approaches
        if (typeof event.created_at === 'number') {
          // Unix timestamp (seconds or milliseconds)
          createdAt = new Date(event.created_at * (event.created_at < 1e10 ? 1000 : 1));
        } else if (typeof event.created_at === 'string') {
          // String timestamp - try ISO format first, then other formats
          const parsed = new Date(event.created_at);
          if (!isNaN(parsed.getTime())) {
            createdAt = parsed;
          } else {
            // Try parsing as GMT format or other common formats
            // If it's already in GMT format like "2024-01-18T02:20:44Z", new Date() should handle it
            console.warn('Failed to parse MetricsLite timestamp:', event.created_at);
            createdAt = new Date(); // Fallback to now
          }
        } else {
          createdAt = new Date();
        }
      } else {
        createdAt = new Date();
      }

      console.log('MetricsLite event timestamp processed:', {
        original: event.created_at,
        parsed: createdAt.toISOString(),
        isValid: !isNaN(createdAt.getTime())
      });

      return {
        id: event.id,
        type: event.type || 'unknown',
        isGoal: event.is_goal || false,
        url: event.url || '',
        value: event.value || 0,
        createdAt,
      };
    });
}

/**
 * Determine if a visitor should trigger lead creation
 */
export function shouldCreateLeadFromVisitor(visitor: MetricsLiteVisitor): boolean {
  const stats = visitor.statistics;

  // Create lead if visitor has:
  // 1. Completed goals
  if (stats.goal_completions && stats.goal_completions > 0) {
    return true;
  }

  // 2. UTM parameters (tracked campaign)
  const leadInfo = visitor.lead_info;
  if (leadInfo.utm_source || leadInfo.utm_campaign) {
    return true;
  }

  // 3. Revenue above threshold ($100)
  if (stats.total_revenue && stats.total_revenue >= 100) {
    return true;
  }

  // 4. High engagement (20+ pageviews)
  if (stats.pageviews && stats.pageviews >= 20) {
    return true;
  }

  return false;
}

/**
 * Extract name parts from visitor name
 */
export function extractNameParts(name?: string): { firstName: string; lastName: string } {
  if (!name) {
    return { firstName: '', lastName: '' };
  }

  const parts = name.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';

  return { firstName, lastName };
}
